import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  Sparkles, CheckCircle2, Clock, Mail, PawPrint, Package, Trash2,
  Loader2, RefreshCw, Save, ExternalLink, AlertTriangle,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const authHeaders = () => {
  const token = localStorage.getItem('admin_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const TikTokLiveAdmin = () => {
  const [submissions, setSubmissions] = useState([]);
  const [enabled, setEnabled] = useState(false);
  const [nextDate, setNextDate] = useState('');
  const [loading, setLoading] = useState(true);
  const [savingDate, setSavingDate] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [listRes, enabledRes, dateRes] = await Promise.all([
        axios.get(`${API}/admin/tiktok-live`, { headers: authHeaders() }),
        axios.get(`${API}/settings/tiktok-live-enabled`),
        axios.get(`${API}/settings/tiktok-live-next-date`),
      ]);
      setSubmissions(listRes.data || []);
      setEnabled(enabledRes.data.enabled);
      setNextDate(dateRes.data.next_date || '');
    } catch (err) {
      toast.error('Failed to load TikTok Live data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const toggleEnabled = async () => {
    try {
      const res = await axios.put(`${API}/admin/settings/tiktok-live-enabled`, {}, { headers: authHeaders() });
      setEnabled(res.data.enabled);
      toast.success(`TikTok Live ${res.data.enabled ? 'enabled' : 'disabled'}`);
    } catch {
      toast.error('Failed to toggle');
    }
  };

  const saveNextDate = async () => {
    setSavingDate(true);
    try {
      await axios.put(`${API}/admin/settings/tiktok-live-next-date`, { next_date: nextDate.trim() || null }, { headers: authHeaders() });
      toast.success('Next live date saved');
    } catch {
      toast.error('Failed to save date');
    } finally {
      setSavingDate(false);
    }
  };

  const toggleReviewed = async (item) => {
    try {
      const res = await axios.patch(`${API}/admin/tiktok-live/${item.id}`, { reviewed: !item.reviewed }, { headers: authHeaders() });
      setSubmissions(s => s.map(x => x.id === item.id ? res.data : x));
    } catch {
      toast.error('Failed to update status');
    }
  };

  const removeItem = async (item) => {
    if (!window.confirm(`Delete submission ${item.reference} for ${item.dog_name}?`)) return;
    try {
      await axios.delete(`${API}/admin/tiktok-live/${item.id}`, { headers: authHeaders() });
      setSubmissions(s => s.filter(x => x.id !== item.id));
      toast.success('Submission deleted');
    } catch {
      toast.error('Failed to delete');
    }
  };

  const pending = submissions.filter(s => !s.reviewed);
  const reviewed = submissions.filter(s => s.reviewed);

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-bold text-[#222] flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[#0FA49C]" />
              TikTok Live Settings
            </h3>
            <p className="text-xs text-[#666] mt-0.5">Control the public TikTok Live submission page.</p>
          </div>
          <button
            onClick={toggleEnabled}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${
              enabled ? 'bg-[#0FA49C] text-white hover:bg-[#0d8f88]' : 'bg-gray-200 text-[#444] hover:bg-gray-300'
            }`}
            data-testid="ttl-admin-toggle"
          >
            {enabled ? (<><CheckCircle2 className="w-4 h-4" /> Live: Enabled</>) : 'Enable Submissions'}
          </button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2">
          <input
            value={nextDate}
            onChange={(e) => setNextDate(e.target.value)}
            placeholder="e.g. Friday 9 May 2026, 7pm"
            className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0FA49C]"
            data-testid="ttl-admin-next-date"
          />
          <button
            onClick={saveNextDate}
            disabled={savingDate}
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-[#1f6d62] hover:bg-[#174f46] text-white text-sm font-semibold disabled:opacity-60"
            data-testid="ttl-admin-save-date"
          >
            {savingDate ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save Date
          </button>
        </div>
      </div>

      {/* List */}
      <div className="flex items-center justify-between">
        <h3 className="font-bold text-[#222]">
          Submissions
          <span className="ml-2 text-xs text-[#666] font-normal">
            ({pending.length} pending · {reviewed.length} reviewed)
          </span>
        </h3>
        <button
          onClick={fetchAll}
          className="inline-flex items-center gap-1.5 text-sm text-[#0FA49C] hover:underline"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {loading ? (
        <div className="text-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[#0FA49C] mx-auto" /></div>
      ) : submissions.length === 0 ? (
        <div className="text-center py-10 bg-white rounded-xl border border-dashed border-gray-200">
          <Sparkles className="w-10 h-10 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-[#666]">No submissions yet</p>
          <p className="text-xs text-[#999]">Submissions will appear here when customers send their bags.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {[...pending, ...reviewed].map(s => (
            <motion.div
              key={s.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className={`bg-white rounded-xl border p-4 ${
                s.reviewed ? 'border-gray-200 opacity-70' : 'border-[#a0da39]/60'
              }`}
              data-testid={`ttl-admin-row-${s.reference}`}
            >
              <div className="flex flex-wrap items-start gap-4">
                {/* Reference */}
                <div className="rounded-xl border-2 border-dashed border-[#a0da39] bg-[#f4f7ea] px-4 py-2 text-center flex-shrink-0">
                  <p className="text-[9px] uppercase tracking-wider text-[#666] font-bold">Ref</p>
                  <p className="text-lg font-extrabold text-[#1f6d62] tracking-widest">{s.reference}</p>
                </div>

                {/* Details */}
                <div className="flex-1 min-w-[180px] text-sm space-y-0.5">
                  <p className="font-bold text-[#222] flex items-center gap-1.5 flex-wrap">
                    <PawPrint className="w-3.5 h-3.5 text-[#0FA49C]" />
                    {s.dog_name}
                    {s.is_partial && (
                      <span
                        className="inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-widest bg-amber-100 text-amber-800 border border-amber-300 px-1.5 py-0.5 rounded-full"
                        title={(s.missing_fields || []).map(f => f.label).join(', ') || 'Missing critical fields'}
                      >
                        <AlertTriangle className="w-2.5 h-2.5" /> Partial
                      </span>
                    )}
                  </p>
                  <p className="text-[#666] flex items-center gap-1.5 text-xs">
                    <Mail className="w-3 h-3" /> {s.email}
                  </p>
                  <p className="text-[#666] flex items-center gap-1.5 text-xs">
                    <Package className="w-3 h-3" />
                    {s.analysis?.brand || '—'} · {s.analysis?.product_name || '—'}
                  </p>
                  <p className="text-[#999] flex items-center gap-1.5 text-xs">
                    <Clock className="w-3 h-3" /> {new Date(s.created_at).toLocaleString()}
                  </p>
                </div>

                {/* Images */}
                <div className="flex gap-2 flex-shrink-0">
                  {s.front_image_url && (
                    <a href={`${BACKEND}${s.front_image_url}`} target="_blank" rel="noreferrer" className="relative block">
                      <img src={`${BACKEND}${s.front_image_url}`} alt="Front" className="w-14 h-14 rounded-lg object-cover border border-gray-200" />
                      <ExternalLink className="absolute -top-1 -right-1 w-3 h-3 bg-white rounded-full text-[#666] p-0.5" />
                    </a>
                  )}
                  {s.back_image_url && (
                    <a href={`${BACKEND}${s.back_image_url}`} target="_blank" rel="noreferrer" className="relative block">
                      <img src={`${BACKEND}${s.back_image_url}`} alt="Back" className="w-14 h-14 rounded-lg object-cover border border-gray-200" />
                      <ExternalLink className="absolute -top-1 -right-1 w-3 h-3 bg-white rounded-full text-[#666] p-0.5" />
                    </a>
                  )}
                </div>

                {/* Actions */}
                <div className="flex flex-col gap-2 flex-shrink-0">
                  <button
                    onClick={() => toggleReviewed(s)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                      s.reviewed
                        ? 'bg-gray-100 text-[#666] hover:bg-gray-200'
                        : 'bg-[#0FA49C] text-white hover:bg-[#0d8f88]'
                    }`}
                    data-testid={`ttl-admin-toggle-reviewed-${s.reference}`}
                  >
                    {s.reviewed ? 'Mark Pending' : 'Mark Reviewed'}
                  </button>
                  <button
                    onClick={() => removeItem(s)}
                    className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-gray-200 text-red-600 hover:bg-red-50"
                    data-testid={`ttl-admin-delete-${s.reference}`}
                  >
                    <Trash2 className="w-3 h-3 inline mr-1" /> Delete
                  </button>
                </div>
              </div>

              {s.analysis?.analytical_constituents && (
                <p className="text-xs text-[#666] mt-2 pl-1 border-l-2 border-gray-200 pl-3">
                  <strong>Nutrition:</strong> {s.analysis.analytical_constituents}
                </p>
              )}
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TikTokLiveAdmin;
